import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { PRICING_TIERS } from '@/data/site';

export default function Pricing({ showHomepageLink = false }) {
  return (
    <section className="section-pad bg-ohana-bg" data-testid="pricing">
      <div className="container-ohana">
        <div className="max-w-3xl">
          <p className="overline">Pricing Guidance</p>
          <h2 className="h-display text-4xl md:text-5xl mt-3 leading-tight">
            Honest ranges. No surprise fees.
          </h2>
          <p className="mt-5 text-ohana-text-muted text-base md:text-lg leading-relaxed">
            Final pricing is confirmed after you send photos. Here is what most jobs land in.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
          {PRICING_TIERS.map((t) => (
            <div
              key={t.name}
              className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${
                t.highlight
                  ? 'bg-ohana-primary text-ohana-bg shadow-2xl shadow-ohana-primary/20 ring-2 ring-ohana-accent'
                  : 'bg-white border border-ohana-border'
              }`}
              data-testid={`pricing-tier-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {t.badge && (
                <span className="absolute -top-3 right-6 inline-flex items-center gap-1.5 rounded-full bg-ohana-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                  <Sparkles className="h-3 w-3" />
                  {t.badge}
                </span>
              )}
              <h3
                className={`font-serif text-2xl ${
                  t.highlight ? 'text-ohana-bg' : 'text-ohana-primary'
                }`}
              >
                {t.name}
              </h3>
              <div
                className={`mt-4 font-serif text-4xl md:text-5xl leading-none ${
                  t.highlight ? 'text-ohana-accent' : 'text-ohana-primary'
                }`}
              >
                {t.price}
              </div>
              <p
                className={`mt-3 text-sm ${
                  t.highlight ? 'text-ohana-bg/75' : 'text-ohana-text-muted'
                }`}
              >
                {t.note}
              </p>

              {t.anchor && (
                <p
                  className={`mt-3 text-sm font-semibold ${
                    t.highlight ? 'text-ohana-accent' : 'text-ohana-accent-dark'
                  }`}
                  data-testid={`pricing-anchor-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {t.anchor}
                </p>
              )}

              <ul className="mt-6 space-y-3">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-4 w-4 flex-none text-ohana-accent" />
                    <span
                      className={`text-sm leading-relaxed ${
                        t.highlight ? 'text-ohana-bg/85' : 'text-ohana-text'
                      }`}
                    >
                      {b}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className={`mt-7 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition ${
                  t.highlight
                    ? 'bg-ohana-accent text-white hover:bg-ohana-accent-dark'
                    : 'bg-ohana-primary text-ohana-bg hover:bg-ohana-primary-dark'
                }`}
                data-testid={`pricing-cta-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {t.cta || 'Get a Quote'}
              </Link>

              {showHomepageLink && (
                <p
                  className={`mt-4 text-xs ${
                    t.highlight ? 'text-ohana-bg/70' : 'text-ohana-text-muted'
                  }`}
                  data-testid={`pricing-homepage-link-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Link
                    to="/"
                    className={`underline underline-offset-4 hover:no-underline ${
                      t.highlight ? 'text-ohana-bg' : 'text-ohana-primary font-semibold'
                    }`}
                  >
                    See full pricing details on our homepage.
                  </Link>
                </p>
              )}

              {t.footnote && (
                <p
                  className={`mt-5 text-xs leading-relaxed ${
                    t.highlight ? 'text-ohana-bg/60' : 'text-ohana-text-muted'
                  }`}
                  data-testid={`pricing-footnote-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {t.footnote}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
